/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgModule } from '@angular/core';
import { AppRoutesModule } from 'app/core/app-routes/app-routes.module';
import { AppComponent } from 'app/core/app-component/app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BlockUIModule } from 'ng-block-ui';
import { LoginComponent } from 'app/core/security/login/login.component';
import { AppAuthGuardService } from 'app/core/security/guards/app-auth-guard.service';
import { AuthService } from 'app/core/security/auth/auth.service';
import { ApiAuthService } from 'app/core/security/api/api-auth.service';
import { SharedModule } from './shared/shared.module';
import { ApiDependenciesService } from './api/services/api-dependencies.service';
import { PermissionsService } from './core/security/permissions/permissions.service';
import { LayoutModule } from 'app/layout/layout.module';
import { FilterFieldsBaseComponent } from 'app/shared/components/list-filter/filter-fields-base.component';
import { AsideService } from './layout/components/aside-container/aside.service';
import { UserPermissionGuardService } from 'app/core/security/guards/user-permission-guard.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { PrintLayoutComponent } from './print/print-layout.component';
import { PrintService } from './print/print.service';
import { UsersService } from './modules/configuration/users/user-management/services/users.service';
import { EditorModule } from '@tinymce/tinymce-angular';
import { AppMaterialCoreModule } from './core/app-material/app-material-core.module';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { NotificationHubService } from './shared/services/signalr.service';
import { OtpComponent } from './core/security/otp/otp.component';
import { OrganiserCreateFormComponent } from './core/security/organiser-onboarding/components/organiser-create-form.component';
import { OrganiserOnboardingService } from './core/security/organiser-onboarding/services/organiser-onboarding-service';
import { AppMaterialModule } from './core/app-material/app-material.module';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaFormsModule, RecaptchaModule, RecaptchaV3Module } from 'ng-recaptcha';
import { environment } from 'environments/environment';
import { ForgotPasswordService } from './core/security/forgot-password/services/forgot-password-service';
import { ResetPasswordFormComponent } from './core/security/forgot-password/components/reset-password-form.component';
import { ForgotPasswordFormComponent } from './core/security/forgot-password/components/forgot-password-form.component';
import { MsalWrapperModule } from './modules/msal-wrapper/msal-wrapper.module';

export const KALEIDA_UK_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MM YYYY',
  },
};


@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        OtpComponent,
        OrganiserCreateFormComponent,
        ForgotPasswordFormComponent,
        ResetPasswordFormComponent,
        FilterFieldsBaseComponent,
        PrintLayoutComponent,
    ],
    bootstrap: [AppComponent],
    imports: [BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        AppMaterialCoreModule,
        MatCheckboxModule,
        //AppMaterialModule,
        BlockUIModule.forRoot(),
        SharedModule,
        // import 3rd party plugin imports after Angular imports
        LayoutModule,
        EditorModule,
        RecaptchaModule,
        RecaptchaFormsModule,
        //RecaptchaV3Module,
        MsalWrapperModule,
        AppRoutesModule // This needs to be last.
    ], providers: [
        AppAuthGuardService,
        AuthService,
        ApiAuthService,
        UserPermissionGuardService,
        ApiDependenciesService,
        PermissionsService,
        AsideService,
        PrintService,
        NotificationHubService,
        UsersService,
        OrganiserOnboardingService,
        ForgotPasswordService,
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: KALEIDA_UK_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
        { provide: HTTP_INTERCEPTORS, useClass: AuthService, multi: true },
        { provide: 'reCAPTCHA_site_key', useValue: environment.googleRecaptchaSiteKey },
        //{ provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.googleRecaptchaSiteKey },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {}
